// @ts-check

import { html } from "lit-html";
import {
  renderCheckboxField,
  renderDetailRow,
  renderErrorMessage,
  renderInputField,
  renderPlaceholder,
  renderSectionCard,
  renderSelectField,
} from "../ui.js";
import {
  formatEuroEs,
  formatSpanishNumber,
  hasText,
  parseNumber,
  parseNumberOrDefault,
} from "../utils.js";

/** @typedef {ReturnType<typeof html>} LitView */
/** @typedef {import("./index.js").ToolEnv} ToolEnv */
/** @typedef {ReturnType<typeof createShopMarginCalculatorState>} ShopMarginCalculatorState */
/** @typedef {{ kind: "empty" } | { kind: "error", error: string } | { kind: "ready", stateTax: number, finalPrice: number, stateConsumerTax: number, priceWithTax: number, shopGrossBenefit: number, impliedMarkupPct: number, impliedRetailMarginPct: number, shopBenefitTax: number, shopBenefitTaxAmount: number, shopNetBenefit: number, yourRevenueBase: number, yourRevenueEffective: number, yourCost: number, shippingCost: number, yourGrossProfitBeforeTax: number, yourProfitTax: number, yourProfitTaxAmount: number, yourNetProfitAfterTax: number, yourMarginPctOnRevenue: number, totalTaxes: number }} ShopMarginResult */

export const shopMarginCalculatorTool = {
  key: "shop",
  path: "/shop-margin-calculator",
  title: "Shop Margin Calculator",
  blurb: "Retail markup, VAT, and net profit breakdowns for shop sales.",
  badge: "Business",
  accentClass: "bg-gradient-to-r from-amber-300 via-orange-300 to-rose-300",
  createState: createShopMarginCalculatorState,
  render: renderShopMarginCalculatorPage,
};

function createShopMarginCalculatorState() {
  return {
    stateTax: "21",
    shopMargin: "",
    marginMode: "markup",
    retailOverrideEnabled: false,
    retailPriceInclVat: "",
    yourCost: "",
    wholesalePrice: "",
    yourProfitTax: "",
    shippingPct: "",
    returnRatePct: "",
    shopBenefitTax: "",
  };
}

/**
 * @param {ShopMarginCalculatorState} toolState
 * @param {ToolEnv} env
 * @returns {LitView}
 */
function renderShopMarginCalculatorPage(toolState, env) {
  const result = computeShopMarginResult(toolState);
  const retailOverrideEnabled = toolState.retailOverrideEnabled;

  return html`
    <section class="grid gap-4 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)] lg:items-start">
      ${renderSectionCard(
        "Calcular beneficios netos",
        "Versión adaptada del experimento original, ahora como vista de la app principal.",
        html`
          <div class="grid gap-4 md:grid-cols-2">
            ${renderInputField({
              label: "IVA (%)",
              value: toolState.stateTax,
              placeholder: "21",
              onInput: function (event) {
                env.handleFieldInput(toolState, "stateTax", event);
              },
            })}
            ${renderInputField({
              label: "Margen de la tienda (%)",
              value: toolState.shopMargin,
              placeholder: "40",
              disabled: retailOverrideEnabled,
              onInput: function (event) {
                env.handleFieldInput(toolState, "shopMargin", event);
              },
            })}
            ${renderSelectField({
              label: "Modo de margen",
              value: toolState.marginMode,
              disabled: retailOverrideEnabled,
              onInput: function (event) {
                env.handleFieldInput(toolState, "marginMode", event);
              },
              options: [
                { value: "markup", label: "Incremento sobre mayorista" },
                { value: "retail_margin", label: "Margen sobre PVP sin IVA" },
              ],
            })}
            ${renderInputField({
              label: "PVP con IVA (anular cálculo)",
              value: toolState.retailPriceInclVat,
              placeholder: "199,99",
              onInput: function (event) {
                env.handleFieldInput(toolState, "retailPriceInclVat", event);
              },
            })}
          </div>

          <div class="mt-4">
            ${renderCheckboxField({
              label: "Usar PVP con IVA manual",
              checked: retailOverrideEnabled,
              help: "Cuando está activo, se ignoran el modo de margen y el margen de la tienda.",
              onInput: function (event) {
                env.handleCheckboxInput(toolState, "retailOverrideEnabled", event);
              },
            })}
          </div>

          <div class="mt-4 grid gap-4 md:grid-cols-2">
            ${renderInputField({
              label: "Tu coste (CMV)",
              value: toolState.yourCost,
              placeholder: "100",
              onInput: function (event) {
                env.handleFieldInput(toolState, "yourCost", event);
              },
            })}
            ${renderInputField({
              label: "Precio mayorista a tienda",
              value: toolState.wholesalePrice,
              placeholder: "150",
              onInput: function (event) {
                env.handleFieldInput(toolState, "wholesalePrice", event);
              },
            })}
            ${renderInputField({
              label: "Impuesto sobre tu beneficio (%)",
              value: toolState.yourProfitTax,
              placeholder: "25",
              onInput: function (event) {
                env.handleFieldInput(toolState, "yourProfitTax", event);
              },
            })}
            ${renderInputField({
              label: "Envío y manipulación (%)",
              value: toolState.shippingPct,
              placeholder: "5",
              onInput: function (event) {
                env.handleFieldInput(toolState, "shippingPct", event);
              },
            })}
            ${renderInputField({
              label: "Tasa de devoluciones (%)",
              value: toolState.returnRatePct,
              placeholder: "0",
              onInput: function (event) {
                env.handleFieldInput(toolState, "returnRatePct", event);
              },
            })}
            ${renderInputField({
              label: "Impuesto sobre beneficio tienda (%)",
              value: toolState.shopBenefitTax,
              placeholder: "25",
              onInput: function (event) {
                env.handleFieldInput(toolState, "shopBenefitTax", event);
              },
            })}
          </div>
        `,
      )}

      <div class="grid gap-4">
        ${renderSectionCard(
          "Definiciones",
          "Resumen rápido de los términos del cálculo.",
          html`
            <div class="grid gap-3 text-sm leading-6 text-stone-600 sm:grid-cols-2">
              <div><strong>IVA:</strong> Impuesto sobre el Valor Añadido.</div>
              <div><strong>PVP:</strong> Precio de Venta al Público.</div>
              <div><strong>CMV:</strong> Coste de Mercancía Vendida.</div>
              <div><strong>Mayorista:</strong> Precio al que vendes a la tienda.</div>
            </div>
          `,
        )}
        ${renderShopMarginResults(result)}
      </div>
    </section>
  `;
}

/**
 * @param {ShopMarginResult} result
 * @returns {LitView}
 */
function renderShopMarginResults(result) {
  if (result.kind === "empty") {
    return renderSectionCard(
      "Resultados",
      "Se calculan automáticamente cuando haya datos mínimos válidos.",
      renderPlaceholder("Introduce al menos tu coste y el precio mayorista para empezar."),
    );
  }

  if (result.kind === "error") {
    return renderSectionCard(
      "Resultados",
      "Corrige el dato marcado y el resumen volverá a aparecer.",
      renderErrorMessage(result.error),
    );
  }

  return renderSectionCard(
    "Resultados",
    "Resumen del PVP, tienda y tus beneficios.",
    html`
      <div class="grid gap-4">
        <section class="rounded-lg bg-stone-100 p-4">
          <h3 class="text-base font-semibold text-stone-900">Precio final</h3>
          <div class="mt-4 grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-[minmax(0,1fr)_auto]">
            ${renderDetailRow("PVP sin IVA", formatEuroEs(result.finalPrice))}
            ${renderDetailRow(`IVA (${formatSpanishNumber(result.stateTax, 2)}%)`, formatEuroEs(result.stateConsumerTax))}
            ${renderDetailRow("PVP con IVA", formatEuroEs(result.priceWithTax), "", true)}
          </div>
        </section>

        <section class="rounded-lg bg-stone-100 p-4">
          <h3 class="text-base font-semibold text-stone-900">Desglose de la tienda</h3>
          <div class="mt-4 grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-[minmax(0,1fr)_auto]">
            ${renderDetailRow("Beneficio bruto tienda", formatEuroEs(result.shopGrossBenefit))}
            ${renderDetailRow("Incremento sobre mayorista", `${formatSpanishNumber(result.impliedMarkupPct, 1)}%`)}
            ${renderDetailRow("Margen sobre PVP sin IVA", `${formatSpanishNumber(result.impliedRetailMarginPct, 1)}%`)}
            ${renderDetailRow(`Impuesto beneficio tienda (${formatSpanishNumber(result.shopBenefitTax, 1)}%)`, formatEuroEs(result.shopBenefitTaxAmount))}
            ${renderDetailRow("Beneficio neto tienda", formatEuroEs(result.shopNetBenefit), "", true)}
          </div>
        </section>

        <section class="rounded-lg bg-stone-100 p-4">
          <h3 class="text-base font-semibold text-stone-900">Tu beneficio</h3>
          <div class="mt-4 grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-[minmax(0,1fr)_auto]">
            ${renderDetailRow("Ingresos mayoristas", formatEuroEs(result.yourRevenueBase))}
            ${renderDetailRow("Ingresos tras devoluciones", formatEuroEs(result.yourRevenueEffective))}
            ${renderDetailRow("Tu coste (CMV)", formatEuroEs(result.yourCost))}
            ${renderDetailRow("Envío y manipulación", formatEuroEs(result.shippingCost))}
            ${renderDetailRow("Beneficio antes de impuestos", formatEuroEs(result.yourGrossProfitBeforeTax))}
            ${renderDetailRow(`Impuesto sobre tu beneficio (${formatSpanishNumber(result.yourProfitTax, 1)}%)`, formatEuroEs(result.yourProfitTaxAmount))}
            ${renderDetailRow("Beneficio neto", formatEuroEs(result.yourNetProfitAfterTax), "", true)}
            ${renderDetailRow("Margen sobre ingresos", `${formatSpanishNumber(result.yourMarginPctOnRevenue, 1)}%`)}
          </div>
        </section>

        <section class="rounded-lg bg-stone-100 p-4">
          <h3 class="text-base font-semibold text-stone-900">Resumen de impuestos</h3>
          <div class="mt-4 grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-[minmax(0,1fr)_auto]">
            ${renderDetailRow("IVA total", formatEuroEs(result.stateConsumerTax))}
            ${renderDetailRow("Impuesto beneficio tienda", formatEuroEs(result.shopBenefitTaxAmount))}
            ${renderDetailRow("Total impuestos", formatEuroEs(result.totalTaxes), "", true)}
          </div>
        </section>
      </div>
    `,
  );
}

/**
 * @param {ShopMarginCalculatorState} toolState
 * @returns {ShopMarginResult}
 */
function computeShopMarginResult(toolState) {
  if (!hasText(toolState.yourCost) || !hasText(toolState.wholesalePrice)) {
    return { kind: "empty" };
  }

  const stateTax = parseNumberOrDefault(toolState.stateTax, 0);
  const shopMargin = parseNumberOrDefault(toolState.shopMargin, 0);
  const yourCost = parseNumber(toolState.yourCost);
  const wholesalePrice = parseNumber(toolState.wholesalePrice);
  const shopBenefitTax = parseNumberOrDefault(toolState.shopBenefitTax, 0);
  const yourProfitTax = parseNumberOrDefault(toolState.yourProfitTax, 0);
  const shippingPct = parseNumberOrDefault(toolState.shippingPct, 0);
  const returnRatePct = parseNumberOrDefault(toolState.returnRatePct, 0);
  const retailPriceInclVat = parseNumber(toolState.retailPriceInclVat);
  const retailOverrideEnabled = Boolean(toolState.retailOverrideEnabled);

  if (!Number.isFinite(yourCost) || yourCost < 0) {
    return { kind: "error", error: "Enter a valid cost (CMV)." };
  }

  if (!Number.isFinite(wholesalePrice) || wholesalePrice <= 0) {
    return { kind: "error", error: "Enter a valid wholesale price." };
  }

  if (retailOverrideEnabled) {
    if (!Number.isFinite(retailPriceInclVat) || retailPriceInclVat <= 0) {
      return {
        kind: "error",
        error: "Retail override enabled: enter a valid VAT-included consumer price.",
      };
    }
  }

  let finalPrice = 0;
  if (retailOverrideEnabled) {
    finalPrice = retailPriceInclVat / (1 + stateTax / 100);
  } else if (toolState.marginMode === "markup") {
    finalPrice = wholesalePrice * (1 + shopMargin / 100);
  } else {
    const denominator = 1 - shopMargin / 100;
    if (denominator <= 0) {
      return { kind: "error", error: "Retail margin must be less than 100%." };
    }

    finalPrice = wholesalePrice / denominator;
  }

  const stateConsumerTax = finalPrice * (stateTax / 100);
  const priceWithTax = finalPrice + stateConsumerTax;
  const shopGrossBenefit = finalPrice - wholesalePrice;
  const impliedMarkupPct = wholesalePrice > 0 ? (shopGrossBenefit / wholesalePrice) * 100 : 0;
  const impliedRetailMarginPct = finalPrice > 0 ? (shopGrossBenefit / finalPrice) * 100 : 0;
  const shopBenefitTaxAmount = shopGrossBenefit * (shopBenefitTax / 100);
  const shopNetBenefit = shopGrossBenefit - shopBenefitTaxAmount;
  const yourRevenueBase = wholesalePrice;
  const yourRevenueEffective = yourRevenueBase * (1 - returnRatePct / 100);
  const shippingCost = yourRevenueBase * (shippingPct / 100);
  const yourGrossProfitBeforeTax = yourRevenueEffective - yourCost - shippingCost;
  const yourProfitTaxAmount = yourGrossProfitBeforeTax > 0
    ? yourGrossProfitBeforeTax * (yourProfitTax / 100)
    : 0;
  const yourNetProfitAfterTax = yourGrossProfitBeforeTax - yourProfitTaxAmount;
  const yourMarginPctOnRevenue = yourRevenueEffective > 0
    ? (yourNetProfitAfterTax / yourRevenueEffective) * 100
    : 0;

  return {
    kind: "ready",
    stateTax,
    finalPrice,
    stateConsumerTax,
    priceWithTax,
    shopGrossBenefit,
    impliedMarkupPct,
    impliedRetailMarginPct,
    shopBenefitTax,
    shopBenefitTaxAmount,
    shopNetBenefit,
    yourRevenueBase,
    yourRevenueEffective,
    yourCost,
    shippingCost,
    yourGrossProfitBeforeTax,
    yourProfitTax,
    yourProfitTaxAmount,
    yourNetProfitAfterTax,
    yourMarginPctOnRevenue,
    totalTaxes: stateConsumerTax + shopBenefitTaxAmount,
  };
}
